// apps/web/src/features/crm/queries.ts — TanStack Query hooks for
//   the `/sources` and `/stages` admin pages (#1080).
//
// @aiInvariant the plaintext HMAC secret returned by `createLeadSource`
//   / `rotateLeadSourceSecret` is intentionally NOT cached in any
//   TanStack Query store. The create/rotate flows therefore deliberately
//   AVOID `useMutation` (whose observer retains the response under
//   `mutation.data`) and use imperative helpers that resolve the secret
//   to the caller, who must surface it through a one-time reveal modal
//   and let it leave React state immediately afterward.
import { useCallback, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateLeadSourceInput,
  LeadSource,
  LeadSourceDeliveryLogQuery,
  LeadSourceSecretReveal,
  LeadStage,
  UpdateLeadSourceInput,
  UpdateLeadStageInput,
} from '@magimp/shared';
import {
  archiveLeadSource,
  createLeadSource,
  fetchLeadSource,
  listLeadSourceDeliveryLogs,
  patchLeadSource,
  rotateLeadSourceSecret,
} from '../../api/lead-sources.js';
import { listLeadStages, patchLeadStage } from '../../api/lead-stages.js';
import { LEAD_SOURCES_QK } from '../../catalog/leadSources.queries.js';

export const LEAD_SOURCE_DETAIL_QK = (code: string) => ['lead-sources', code] as const;
export const LEAD_SOURCE_LOGS_QK = (code: string) =>
  ['lead-sources', code, 'delivery-logs'] as const;
export const LEAD_STAGES_QK = ['lead-stages'] as const;

export function useLeadSourceDetail(code: string | null) {
  return useQuery({
    queryKey: LEAD_SOURCE_DETAIL_QK(code ?? ''),
    queryFn: () => fetchLeadSource(code!),
    enabled: code != null,
  });
}

export function useLeadSourceDeliveryLogs(
  code: string | null,
  query: Partial<LeadSourceDeliveryLogQuery> = {},
) {
  return useQuery({
    queryKey: [...LEAD_SOURCE_LOGS_QK(code ?? ''), query.outcome ?? null, query.limit ?? null],
    queryFn: () => listLeadSourceDeliveryLogs(code!, query),
    enabled: code != null,
  });
}

// WHY (#1080): the create + rotate flows return a one-time plaintext
//   HMAC secret. Using `useMutation` here would retain that envelope on
//   the mutation observer's `data` after success, exposing the secret
//   to React-Query devtools, observers, and any later mount of the same
//   hook. We bypass `useMutation` and provide our own thin imperative
//   wrapper that resolves the secret directly to the caller and never
//   parks it in cache.
export function useCreateLeadSource(): {
  mutateAsync: (body: CreateLeadSourceInput) => Promise<LeadSourceSecretReveal>;
  isPending: boolean;
} {
  const qc = useQueryClient();
  const [isPending, setPending] = useState(false);
  const mutateAsync = useCallback(
    async (body: CreateLeadSourceInput): Promise<LeadSourceSecretReveal> => {
      setPending(true);
      try {
        const reveal = await createLeadSource(body);
        await qc.invalidateQueries({ queryKey: LEAD_SOURCES_QK });
        return reveal;
      } finally {
        setPending(false);
      }
    },
    [qc],
  );
  return { mutateAsync, isPending };
}

export function usePatchLeadSource(code: string) {
  const qc = useQueryClient();
  return useMutation<LeadSource, unknown, UpdateLeadSourceInput>({
    mutationFn: (body) => patchLeadSource(code, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: LEAD_SOURCES_QK });
      void qc.invalidateQueries({ queryKey: LEAD_SOURCE_DETAIL_QK(code) });
    },
  });
}

export function useRotateLeadSourceSecret(code: string): {
  mutateAsync: () => Promise<LeadSourceSecretReveal>;
  isPending: boolean;
} {
  const qc = useQueryClient();
  const [isPending, setPending] = useState(false);
  const mutateAsync = useCallback(async (): Promise<LeadSourceSecretReveal> => {
    setPending(true);
    try {
      const reveal = await rotateLeadSourceSecret(code);
      await qc.invalidateQueries({ queryKey: LEAD_SOURCES_QK });
      await qc.invalidateQueries({ queryKey: LEAD_SOURCE_DETAIL_QK(code) });
      return reveal;
    } finally {
      setPending(false);
    }
  }, [qc, code]);
  return { mutateAsync, isPending };
}

export function useArchiveLeadSource(code: string) {
  const qc = useQueryClient();
  return useMutation<LeadSource, unknown, void>({
    mutationFn: () => archiveLeadSource(code),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: LEAD_SOURCES_QK });
      void qc.invalidateQueries({ queryKey: LEAD_SOURCE_DETAIL_QK(code) });
    },
  });
}

export function useLeadStages() {
  return useQuery({
    queryKey: LEAD_STAGES_QK,
    queryFn: () => listLeadStages(),
  });
}

export function usePatchLeadStage(code: string) {
  const qc = useQueryClient();
  return useMutation<LeadStage, unknown, UpdateLeadStageInput>({
    mutationFn: (body) => patchLeadStage(code, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: LEAD_STAGES_QK });
    },
  });
}
